import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AppsIcon from "@mui/icons-material/Apps";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import { useMsal } from "@azure/msal-react";
import { jwtDecode } from "jwt-decode";
import { APPLICATION_LIST } from "../../constants/userAccess";
import {
  createAdminAccessRecord,
  deleteAdminAccessRecord,
  fetchAdminAccessRecords,
  searchUserName,
  updateAdminAccessRecord,
} from "../../services/OutboundOrders/UserRolesServices";
import debounce from "lodash/debounce";
import { createFilterOptions } from "@mui/material/Autocomplete";

const userFilter = createFilterOptions();

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const getUserRolesFromToken = (accessToken) => {
  if (!accessToken) return [];
  try {
    const decoded = jwtDecode(accessToken);
    const roles = [];
    if (Array.isArray(decoded.roles)) roles.push(...decoded.roles);
    if (decoded.role) roles.push(decoded.role);
    if (decoded.authorities) {
      roles.push(
        ...(Array.isArray(decoded.authorities)
          ? decoded.authorities
          : [decoded.authorities]),
      );
    }
    if (decoded.permissions) {
      roles.push(
        ...(Array.isArray(decoded.permissions)
          ? decoded.permissions
          : [decoded.permissions]),
      );
    }
    if (decoded.scp) roles.push(...String(decoded.scp).split(" "));

    return roles
      .filter(Boolean)
      .map((role) => String(role).trim().toUpperCase());
  } catch (error) {
    console.error("Failed to decode JWT token", error);
    return [];
  }
};

const AccessControl = () => {
  const { instance, accounts } = useMsal();

  // Form State
  const [application, setApplication] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");

  // Edit Modal State
  const [editingRecord, setEditingRecord] = useState(null);
  const [editApplication, setEditApplication] = useState("");
  const [editAdminName, setEditAdminName] = useState("");
  const [editAdminEmail, setEditAdminEmail] = useState("");
  const [editUserOptions, setEditUserOptions] = useState([]);
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Data & Status State
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedRecordToDelete, setSelectedRecordToDelete] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editError, setEditError] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Autocomplete Search State (Create Form)
  const [userOptions, setUserOptions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);

  const currentUserEmail = useMemo(() => {
    return (accounts?.[0]?.username || accounts?.[0]?.name || "")
      .toLowerCase()
      .trim();
  }, [accounts]);

  const fetchUsersByName = async (searchText, isEdit = false) => {
    if (!searchText || searchText.length < 2) return;
    if (isEdit) setEditUserLoading(true);
    else setUserLoading(true);

    try {
      const users = await searchUserName(searchText);
      if (isEdit) setEditUserOptions(Array.isArray(users) ? users : []);
      else setUserOptions(Array.isArray(users) ? users : []);
    } catch (e) {
      console.error("User search failed", e);
      if (isEdit) setEditUserOptions([]);
      else setUserOptions([]);
    } finally {
      if (isEdit) setEditUserLoading(false);
      else setUserLoading(false);
    }
  };

  const debouncedFetchUsers = useMemo(
    () => debounce((text) => fetchUsersByName(text, false), 300),
    [],
  );

  const debouncedFetchEditUsers = useMemo(
    () => debounce((text) => fetchUsersByName(text, true), 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedFetchUsers.cancel();
      debouncedFetchEditUsers.cancel();
    };
  }, [debouncedFetchUsers, debouncedFetchEditUsers]);

  const fetchRecords = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await fetchAdminAccessRecords();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch owner records", err);
      setError("Failed to load owner records.");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkAccess = async () => {
      try {
        if (!accounts?.[0]) {
          setIsAuthorized(false);
          return;
        }

        const tokenResponse = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: [`${process.env.REACT_APP_CLIENT_ID}/.default`],
        });

        const roles = getUserRolesFromToken(tokenResponse.accessToken);

        if (roles.includes("UAM_ADMIN")) {
          setIsAuthorized(true);
          await fetchRecords();
          return;
        }

        setIsAuthorized(false);
      } catch (err) {
        console.error("Unable to validate owner access", err);
      }
    };

    checkAccess();
  }, [accounts, instance, currentUserEmail]);

  const handleAddAdmin = async () => {
    const trimmedName = adminName.trim();
    const trimmedEmail = adminEmail.trim().toLowerCase();

    if (!application) {
      setError("Application is a mandatory field.");
      setSuccess("");
      return;
    }
    if (!trimmedName) {
      setError("Owner Name is a mandatory field.");
      setSuccess("");
      return;
    }
    if (!trimmedEmail) {
      setError("Email ID is a mandatory field.");
      setSuccess("");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      setSuccess("");
      return;
    }

    const isDuplicate = records.some(
      (row) =>
        row.application &&
        row.application.trim() === application.trim() &&
        row.adminEmail &&
        row.adminEmail.toLowerCase().trim() === trimmedEmail,
    );

    if (isDuplicate) {
      setError(
        `Record for email "${trimmedEmail}" and application "${application}" already exists.`,
      );
      setSuccess("");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    const payload = {
      adminName: trimmedName,
      adminEmail: trimmedEmail,
      application,
    };

    try {
      await createAdminAccessRecord(payload);
      setSuccess("Owner added successfully.");
      setAdminName("");
      setAdminEmail("");
      setUserOptions([]);
      await fetchRecords();
    } catch (err) {
      console.error("Failed to create owner record", err);
      const backendError =
        err?.response?.data?.message ||
        "Unable to add owner. Please try again.";
      setError(backendError);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEditModal = (row) => {
    setEditingRecord(row);
    setEditApplication(row.application || "");
    setEditAdminName(row.adminName || "");
    setEditAdminEmail(row.adminEmail || "");
    setEditError("");
  };

  const handleUpdateAdmin = async () => {
    const trimmedName = editAdminName.trim();
    const trimmedEmail = editAdminEmail.trim().toLowerCase();

    if (!editApplication) {
      setEditError("Application is a mandatory field.");
      return;
    }
    if (!trimmedName) {
      setEditError("Owner Name is a mandatory field.");
      return;
    }
    if (!trimmedEmail) {
      setEditError("Email ID is a mandatory field.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setEditError("Please enter a valid email address.");
      return;
    }

    const isDuplicate = records.some(
      (row) =>
        row.id !== editingRecord.id &&
        row.application &&
        row.application.trim() === editApplication.trim() &&
        row.adminEmail &&
        row.adminEmail.toLowerCase().trim() === trimmedEmail,
    );

    if (isDuplicate) {
      setEditError(
        `Record for email "${trimmedEmail}" and application "${editApplication}" already exists.`,
      );
      return;
    }

    setUpdating(true);
    setEditError("");

    const payload = {
      adminName: trimmedName,
      adminEmail: trimmedEmail,
      application: editApplication,
    };

    try {
      await updateAdminAccessRecord(editingRecord.id, payload);
      setSuccess("Owner access updated successfully.");
      setEditingRecord(null);
      await fetchRecords();
    } catch (err) {
      console.error("Failed to update owner access record", err);
      const backendError =
        err?.response?.data?.message ||
        "Unable to update owner. Please try again.";
      setEditError(backendError);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRecordToDelete?.id) return;

    setDeletingId(selectedRecordToDelete.id);
    setError("");
    setSuccess("");

    try {
      await deleteAdminAccessRecord(selectedRecordToDelete.id);
      setSuccess(
        `Successfully deleted access for ${selectedRecordToDelete.adminEmail}.`,
      );
      setSelectedRecordToDelete(null);
      await fetchRecords();
    } catch (err) {
      console.error("Failed to delete owner access record", err);
      setError("Failed to delete owner access record. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredRecords = useMemo(() => {
    if (!application) {
      return records;
    }
    return records.filter(
      (row) =>
        row.application && row.application.trim() === application.trim(),
    );
  }, [records, application]);

  useEffect(() => {
    setPage(0);
  }, [application]);

  const paginatedRecords = useMemo(() => {
    return filteredRecords.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredRecords, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const tableColumns = useMemo(
    () => [
      { label: "Application", key: "application", width: "25%" },
      { label: "Owner Name", key: "adminName", width: "20%" },
      { label: "Email", key: "adminEmail", width: "25%" },
      { label: "Last Modified On", key: "createdDate", width: "20%" },
      { label: "Actions", key: "actions", align: "center", width: "10%" },
    ],
    [],
  );

  if (!isAuthorized && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Access denied. This page is available only to users with the UAM_ADMIN
          role.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        minHeight: "100vh",
        fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      {/* 1. Red Gradient Top Header Banner */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background:
            "linear-gradient(90deg, #8E0E14 0%, #F40009 50%, #D40010 100%)",
          color: "#ffffff",
          px: { xs: 3, sm: 4 },
          pt: 3,
          pb: 4,
          mt: 0,
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-96px",
            left: "33%",
            width: "60%",
            height: "224px",
            borderRadius: "50%",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            filter: "blur(48px)",
            transform: "rotate(12deg)",
            pointerEvents: "none",
          }}
        />

        <Box sx={{ position: "relative", zIndex: 10 }}>
          <Typography
            variant="h1"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.8,
              fontSize: "0.68rem",
              fontWeight: 500,
              letterSpacing: "3px",
              textTransform: "uppercase",
              opacity: 0.9,
              mb: 0.5,
              mt: 2,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#ffffff",
              }}
            />
            USER ACCESS MANAGEMENT
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.5rem", sm: "1.9rem" },
              letterSpacing: "-0.025em",
              color: "#000000",
            }}
          >
            Admin Portal
          </Typography>
        </Box>

        <Box
          component="svg"
          viewBox="0 0 1440 48"
          preserveAspectRatio="none"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "36px",
            color: "rgba(255, 255, 255, 0.10)",
            fill: "currentColor",
            pointerEvents: "none",
          }}
        >
          <path d="M0,10 C 260,42 480,0 760,14 C 1040,28 1240,2 1440,12 L1440,48 L0,48 Z" />
        </Box>

        <Box
          component="svg"
          viewBox="0 0 1440 48"
          preserveAspectRatio="none"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "28px",
            color: "rgba(255, 255, 255, 0.16)",
            fill: "currentColor",
            pointerEvents: "none",
          }}
        >
          <path d="M0,24 C 220,48 460,0 720,18 C 980,36 1220,4 1440,22 L1440,48 L0,48 Z" />
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ padding: "24px 32px" }}>
        {/* 2. First Box: Configure Owners */}
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderTop: "3px solid #dc2626",
            borderRadius: "16px",
            p: "20px 24px",
            mb: "32px",
            boxShadow: "0 10px 25px rgba(220, 38, 38, 0.12)",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              fontSize: "13px",
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: "20px",
              display: "flex",
              alignItems: "center",
              lineHeight: 1,
              "&::before": {
                content: '""',
                display: "inline-block",
                width: "4px",
                height: "16px",
                backgroundColor: "#dc2626",
                borderRadius: "2px",
                mr: "8px",
              },
            }}
          >
            Configure Owners
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "20px",
            }}
          >
            {/* Application Dropdown */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flex: "1 1 230px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#334155",
                  whiteSpace: "nowrap",
                  mr: "8px",
                }}
              >
                Application:
              </Typography>
              <AppsIcon sx={{ fontSize: "16px", color: "#dc2626", mr: "8px" }} />
              <FormControl size="small" required fullWidth>
                <Select
                  value={application}
                  displayEmpty
                  onChange={(event) => setApplication(event.target.value)}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography sx={{ color: "#9ca3af", fontSize: "12px" }}>
                          Application *
                        </Typography>
                      );
                    }
                    return selected;
                  }}
                  sx={{
                    height: "34px",
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    fontSize: "12px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#d1d5db",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#9ca3af",
                    },
                  }}
                >
                  <MenuItem disabled value="">
                    <em style={{ fontSize: "12px" }}>Application *</em>
                  </MenuItem>
                  {APPLICATION_LIST.map((item) => (
                    <MenuItem key={item} value={item} sx={{ fontSize: "12px" }}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Owner Name Search Field */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flex: "1 1 260px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#334155",
                  whiteSpace: "nowrap",
                  mr: "8px",
                }}
              >
                Owner Name:
              </Typography>
              <PersonIcon sx={{ fontSize: "16px", color: "#dc2626", mr: "8px" }} />
              <Autocomplete
                freeSolo
                fullWidth
                size="small"
                options={userOptions}
                loading={userLoading}
                value={adminName}
                noOptionsText="Type to search or add user"
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  if (option.inputValue) return option.inputValue;
                  return option.userName || "";
                }}
                filterOptions={(options, params) => {
                  const filtered = userFilter(options, params);
                  const { inputValue } = params;
                  const isExisting = options.some(
                    (opt) =>
                      opt.userName?.toLowerCase() === inputValue.toLowerCase(),
                  );
                  if (inputValue.length >= 2 && !isExisting) {
                    filtered.push({
                      inputValue,
                      userName: `Add "${inputValue}"`,
                      userEmail: "",
                      isNewUser: true,
                    });
                  }
                  return filtered;
                }}
                onInputChange={(e, value, reason) => {
                  if (reason !== "input") return;
                  if (!value || value.length < 2) {
                    setUserOptions([]);
                    return;
                  }
                  debouncedFetchUsers(value);
                }}
                onChange={(e, newValue) => {
                  if (!newValue) {
                    setAdminName("");
                    setAdminEmail("");
                    return;
                  }
                  if (typeof newValue === "string") {
                    setAdminName(newValue);
                    return;
                  }
                  if (newValue.inputValue || newValue.isNewUser) {
                    setAdminName(newValue.inputValue);
                    setAdminEmail("");
                    return;
                  }
                  setAdminName(newValue.userName || "");
                  setAdminEmail(newValue.userEmail || "");
                }}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return (
                    <li key={key} {...rest}>
                      {option.inputValue ? (
                        <Typography
                          variant="body2"
                          sx={{ fontStyle: "italic", color: "primary.main", fontSize: "12px" }}
                        >
                          + Add "{option.inputValue}"
                        </Typography>
                      ) : (
                        <Box>
                          <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px" }}>
                            {option.userName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>
                            {option.userEmail}
                          </Typography>
                        </Box>
                      )}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Owner Name *"
                    required
                    sx={{
                      backgroundColor: "#ffffff",
                      borderRadius: "8px",
                      "& .MuiInputBase-root": {
                        height: "34px",
                        fontSize: "12px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d1d5db",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#9ca3af",
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {userLoading && <CircularProgress size={16} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Email ID Field */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flex: "1 1 260px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#334155",
                  whiteSpace: "nowrap",
                  mr: "8px",
                }}
              >
                Email ID:
              </Typography>
              <EmailIcon sx={{ fontSize: "16px", color: "#dc2626", mr: "8px" }} />
              <TextField
                placeholder="Email ID *"
                size="small"
                required
                fullWidth
                value={adminEmail}
                onChange={(event) => setAdminEmail(event.target.value)}
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  "& .MuiInputBase-root": {
                    height: "34px",
                    fontSize: "12px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#d1d5db",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#9ca3af",
                  },
                }}
              />
            </Box>

            {/* Add Owner Button */}
            <Button
              variant="contained"
              onClick={handleAddAdmin}
              disabled={saving}
              sx={{
                backgroundColor: "#dc2626",
                color: "#ffffff",
                minWidth: 130,
                height: 34,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "12px",
                borderRadius: "8px",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                "&:hover": { backgroundColor: "#b91c1c" },
                "&:disabled": { backgroundColor: "#ccc", color: "#666" },
              }}
            >
              {saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "ADD OWNER"
              )}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 1.5, py: 0.5, fontSize: "12px" }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 1.5, py: 0.5, fontSize: "12px" }}>
              {success}
            </Alert>
          )}
        </Box>

        {/* 3. Second Box: Owners Table Container (Internal Scroll) */}
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderTop: "3px solid #dc2626",
            borderRadius: "16px",
            p: "20px 24px 0px 24px",
            boxShadow: "0 10px 25px rgba(220, 38, 38, 0.12)",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              fontSize: "13px",
              color: "#475569",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: "18px",
              display: "flex",
              alignItems: "center",
              lineHeight: 1,
              "&::before": {
                content: '""',
                display: "inline-block",
                width: "4px",
                height: "16px",
                backgroundColor: "#dc2626",
                borderRadius: "2px",
                mr: "8px",
              },
            }}
          >
            Owners Table
          </Typography>

          {/* Table Section with Internal Scrolling Props */}
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              borderRadius: "12px",
              borderColor: "#fecdd3",
              maxHeight: "calc(100vh - 280px)",
              overflow: "auto",
              boxShadow: "none",
            }}
          >
            <Table stickyHeader sx={{ width: "100%", tableLayout: "fixed" }}>
              <TableHead sx={{ backgroundColor: "#fff0f2" }}>
                <TableRow>
                  {tableColumns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align || "left"}
                      style={{ width: column.width }}
                      sx={{
                        fontWeight: 600,
                        color: "#881337",
                        textTransform: "uppercase",
                        fontSize: "0.75rem",
                        py: "8px",
                        px: 1.5,
                        letterSpacing: "0.4px",
                        backgroundColor: "#fff0f2",
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow style={{ height: 100 }}>
                    <TableCell
                      colSpan={tableColumns.length}
                      align="center"
                      sx={{ py: 2 }}
                    >
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                ) : filteredRecords.length === 0 ? (
                  <TableRow style={{ height: 100 }}>
                    <TableCell
                      colSpan={tableColumns.length}
                      align="center"
                      sx={{ py: 2, color: "text.secondary", fontSize: "12px" }}
                    >
                      No owner access records found.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRecords.map((row) => (
                    <TableRow
                      key={row.id ?? `${row.adminEmail}-${row.application}`}
                      hover
                      sx={{
                        "&:hover": { backgroundColor: "#f8fafc" },
                        "& td": { py: "8px", px: 1.5, fontSize: "12px" },
                      }}
                    >
                      <TableCell
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.application || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.adminName || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.adminEmail || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatDate(row.createdDate)}
                      </TableCell>
                      <TableCell align="center">
                        {/* Red Edit Icon Button */}
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleOpenEditModal(row)}
                          sx={{ mr: 0.5, p: "2px" }}
                        >
                          <EditIcon sx={{ fontSize: "16px" }} />
                        </IconButton>
                        {/* Red Delete Icon Button */}
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => setSelectedRecordToDelete(row)}
                          disabled={deletingId === row.id}
                          sx={{ p: "2px" }}
                        >
                          {deletingId === row.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <DeleteIcon sx={{ fontSize: "16px" }} />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredRecords.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              mt: 0.5,
              borderTop: "none",
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  fontSize: "12px",
                },
            }}
          />
        </Box>
      </Box>

      {/* Edit Owner Access Modal */}
      <Dialog
        open={Boolean(editingRecord)}
        onClose={() => setEditingRecord(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600 }}>
          Edit Owner Access
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl size="small" required fullWidth>
              <Select
                value={editApplication}
                displayEmpty
                onChange={(event) => setEditApplication(event.target.value)}
                sx={{ fontSize: "12px", backgroundColor: "#ffffff", borderRadius: "8px" }}
              >
                <MenuItem disabled value="">
                  <em style={{ fontSize: "12px" }}>Application *</em>
                </MenuItem>
                {APPLICATION_LIST.map((item) => (
                  <MenuItem key={item} value={item} sx={{ fontSize: "12px" }}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              freeSolo
              fullWidth
              size="small"
              options={editUserOptions}
              loading={editUserLoading}
              value={editAdminName}
              noOptionsText="Type to search user"
              getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                if (option.inputValue) return option.inputValue;
                return option.userName || "";
              }}
              filterOptions={(options, params) => {
                const filtered = userFilter(options, params);
                const { inputValue } = params;
                const isExisting = options.some(
                  (opt) =>
                    opt.userName?.toLowerCase() === inputValue.toLowerCase(),
                );
                if (inputValue.length >= 2 && !isExisting) {
                  filtered.push({
                    inputValue,
                    userName: `Add "${inputValue}"`,
                    userEmail: "",
                    isNewUser: true,
                  });
                }
                return filtered;
              }}
              onInputChange={(e, value, reason) => {
                if (reason !== "input") return;
                if (!value || value.length < 2) {
                  setEditUserOptions([]);
                  return;
                }
                debouncedFetchEditUsers(value);
              }}
              onChange={(e, newValue) => {
                if (!newValue) {
                  setEditAdminName("");
                  setEditAdminEmail("");
                  return;
                }
                if (typeof newValue === "string") {
                  setEditAdminName(newValue);
                  return;
                }
                if (newValue.inputValue || newValue.isNewUser) {
                  setEditAdminName(newValue.inputValue);
                  setEditAdminEmail("");
                  return;
                }
                setEditAdminName(newValue.userName || "");
                setEditAdminEmail(newValue.userEmail || "");
              }}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <li key={key} {...rest}>
                    {option.inputValue ? (
                      <Typography
                        variant="body2"
                        sx={{ fontStyle: "italic", color: "primary.main", fontSize: "12px" }}
                      >
                        + Add "{option.inputValue}"
                      </Typography>
                    ) : (
                      <Box>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px" }}>
                          {option.userName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>
                          {option.userEmail}
                        </Typography>
                      </Box>
                    )}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Owner Name *"
                  required
                  sx={{
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    "& .MuiInputBase-root": { fontSize: "12px" },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {editUserLoading && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              placeholder="Email ID *"
              size="small"
              required
              fullWidth
              value={editAdminEmail}
              onChange={(event) => setEditAdminEmail(event.target.value)}
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                "& .MuiInputBase-root": { fontSize: "12px" },
              }}
            />

            {editError && <Alert severity="error" sx={{ fontSize: "12px" }}>{editError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingRecord(null)} color="inherit" sx={{ fontSize: "12px" }}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateAdmin}
            variant="contained"
            disabled={updating}
            sx={{
              backgroundColor: "#dc2626",
              color: "#ffffff",
              fontSize: "12px",
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#b91c1c" },
            }}
          >
            {updating ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={Boolean(selectedRecordToDelete)}
        onClose={() => setSelectedRecordToDelete(null)}
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600 }}>
          Confirm Revoke Access
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "12px" }}>
            Are you sure you want to remove owner access for{" "}
            <strong>{selectedRecordToDelete?.adminEmail}</strong> for application{" "}
            <strong>{selectedRecordToDelete?.application}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSelectedRecordToDelete(null)}
            color="inherit"
            sx={{ fontSize: "12px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={Boolean(deletingId)}
            sx={{ fontSize: "12px", borderRadius: "8px" }}
          >
            {deletingId ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessControl;